import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/context/ThemeContext";
import { toast } from "sonner";
import { Plus, Check } from "lucide-react";
import { useSectionsEnabled, setSectionsEnabled } from "@/lib/sections";

export const DEFAULT_SECTIONS = ["A", "B", "C"];
const ALL = "__all__";

// Branch + class + section picker shared by class-based pages.
export function ClassPicker({ value, onChange, allowAllSections = false }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [types, setTypes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newSec, setNewSec] = useState("");
  const global = isAdminRole(user?.role);
  const useSec = useSectionsEnabled();

  useEffect(() => {
    Promise.all([api.get("/branches"), api.get("/meta/school-types")]).then(([b, t]) => {
      setBranches(b.data);
      setTypes(t.data);
      if (!value.branch_id) {
        const branch_id = user?.branch_id || b.data[0]?.id || "";
        onChange({ branch_id, class_name: user?.class_name || "", section: user?.section || (allowAllSections ? "" : "A") });
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const branch = branches.find((b) => b.id === value.branch_id);
  const classes = types.find((t) => t.id === branch?.school_type)?.classes || [];
  const sections = branch?.sections?.length ? branch.sections : DEFAULT_SECTIONS;

  async function addSection() {
    const name = newSec.trim().toUpperCase();
    if (!name || !branch) return;
    try {
      const { data } = await api.put(`/branches/${branch.id}/sections`, { sections: [...sections, name] });
      setBranches(branches.map((b) => (b.id === branch.id ? { ...b, sections: data.sections } : b)));
      onChange({ ...value, section: name });
      setNewSec(""); setAdding(false);
      toast.success(`Section ${name} added`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {global && (
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">Branch</Label>
          <Select value={value.branch_id} onValueChange={(v) => onChange({ ...value, branch_id: v, class_name: "" })}>
            <SelectTrigger data-testid="picker-branch" className="mt-1 bg-white"><SelectValue placeholder="Branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs">Class</Label>
        <Select value={value.class_name} onValueChange={(v) => onChange({ ...value, class_name: v })}>
          <SelectTrigger data-testid="picker-class" className="mt-1 bg-white"><SelectValue placeholder="Class" /></SelectTrigger>
          <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {useSec && <div>
        <Label className="text-xs">Section</Label>
        {adding ? (
          <div className="flex gap-1 mt-1">
            <Input data-testid="picker-new-section" autoFocus value={newSec} onChange={(e) => setNewSec(e.target.value)} placeholder="e.g. D or Rose" className="bg-white" />
            <Button data-testid="picker-new-section-save" type="button" size="icon" onClick={addSection} className="btn-brand shrink-0"><Check size={15} /></Button>
          </div>
        ) : (
          <div className="flex gap-1 mt-1">
            <Select value={value.section || (allowAllSections ? ALL : "")} onValueChange={(v) => onChange({ ...value, section: v === ALL ? "" : v })}>
              <SelectTrigger data-testid="picker-section" className="bg-white"><SelectValue placeholder="Section" /></SelectTrigger>
              <SelectContent>
                {allowAllSections && <SelectItem value={ALL}>All sections</SelectItem>}
                {sections.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {global && <Button data-testid="picker-add-section" type="button" size="icon" variant="outline" onClick={() => setAdding(true)} className="shrink-0" title="Add section"><Plus size={15} /></Button>}
          </div>
        )}
      </div>}
    </div>
  );
}

export function SectionsManager() {
  const on = useSectionsEnabled();
  const [branches, setBranches] = useState([]);
  const [draft, setDraft] = useState({});
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);

  async function save(b, list) {
    try {
      const { data } = await api.put(`/branches/${b.id}/sections`, { sections: list });
      setBranches(branches.map((x) => (x.id === b.id ? { ...x, sections: data.sections } : x)));
      toast.success("Sections updated");
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggle() {
    try { const { data } = await api.put("/settings/sections", { enabled: !on }); setSectionsEnabled(data.enabled); toast.success(data.enabled ? "Sections turned on" : "Sections hidden everywhere"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div data-testid="sections-manager" className="bg-white rounded-2xl shadow-sm p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-1"><div className="font-display font-bold text-slate-900">Class sections</div>
          <p className="text-xs text-slate-500">{on ? "Section dropdowns are shown. Name them your way for each branch (e.g. Rose, Lily)." : "Sections are hidden across the app. Turn on if your classes are split into sections."}</p></div>
        <Button data-testid="sections-toggle" variant={on ? "outline" : "default"} className={on ? "" : "btn-brand"} onClick={toggle}>{on ? "Turn off sections" : "Turn on sections"}</Button>
      </div>
      {on && branches.map((b) => {
        const list = b.sections?.length ? b.sections : DEFAULT_SECTIONS;
        return (
          <div key={b.id} className="border-t border-slate-100 pt-3">
            <div className="text-sm font-semibold text-slate-800 mb-2">{b.name}</div>
            <div className="flex flex-wrap gap-2 items-center">
              {list.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-3 py-1 text-sm text-violet-800">
                  {s}
                  {list.length > 1 && <button data-testid={`remove-section-${b.code}-${s}`} onClick={() => save(b, list.filter((x) => x !== s))} className="text-violet-400 hover:text-rose-600">×</button>}
                </span>
              ))}
              <Input data-testid={`new-section-${b.code}`} value={draft[b.id] || ""} onChange={(e) => setDraft({ ...draft, [b.id]: e.target.value })} placeholder="New section" className="w-32 h-8" />
              <Button data-testid={`add-section-${b.code}`} size="sm" variant="outline" disabled={!draft[b.id]?.trim()} onClick={() => { save(b, [...list, draft[b.id]]); setDraft({ ...draft, [b.id]: "" }); }}><Plus size={14} /></Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
